import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/Button/Button';
import { TextField } from '@/components/TextField/TextField';
import { AuthCard } from '@/features/auth';
import { verifyEmail, resendVerification } from '@/features/auth/api';
import { ApiError } from '@/lib/api';
import { t } from '@/lib/i18n';

type PageState = 'pending' | 'confirming' | 'expired' | 'used' | 'error';

const resendSchema = z.object({
  email: z.string().email('Please enter a valid email address.'),
});
type ResendValues = z.infer<typeof resendSchema>;

export default function VerifyEmailRoute() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') ?? '';
  const [pageState, setPageState] = useState<PageState>('pending');
  const [resendSent, setResendSent] = useState(false);

  const resendForm = useForm<ResendValues>({ resolver: zodResolver(resendSchema) });

  async function handleConfirm() {
    setPageState('confirming');
    try {
      await verifyEmail(token);
      void navigate('/login', { state: { verified: true } });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.code === 'EMAIL_TOKEN_EXPIRED' || err.code === 'EMAIL_TOKEN_NOT_FOUND') {
          setPageState('expired');
        } else if (err.code === 'EMAIL_TOKEN_USED') {
          setPageState('used');
        } else {
          setPageState('error');
        }
      } else {
        setPageState('error');
      }
    }
  }

  async function handleResend(values: ResendValues) {
    await resendVerification(values.email);
    setResendSent(true);
  }

  if (!token) {
    return (
      <AuthCard title={t('Invalid link')} tagline={t('Build a bigger team. Stay your size.')}>
        <p style={{ fontSize: 14, color: 'var(--color-text-muted)' }}>
          {t('This link is missing a token.')} <Link to="/signup">{t('Start over →')}</Link>
        </p>
      </AuthCard>
    );
  }

  if (pageState === 'used') {
    return (
      <AuthCard title={t('Link already used')} tagline={t('Build a bigger team. Stay your size.')}>
        <p style={{ fontSize: 14, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
          {t(
            'This link has already been used. Try signing in, or request a new confirmation link.',
          )}
        </p>
        <div style={{ marginTop: 20 }}>
          <Button variant="primary" size="lg" onClick={() => void navigate('/login')}>
            {t('Sign in →')}
          </Button>
        </div>
      </AuthCard>
    );
  }

  if (pageState === 'expired') {
    if (resendSent) {
      return (
        <AuthCard title={t('New link sent')} tagline={t('Build a bigger team. Stay your size.')}>
          <p style={{ fontSize: 14, color: 'var(--color-text-muted)', lineHeight: 1.6 }}>
            {t('Check your inbox for a new confirmation link.')}
          </p>
        </AuthCard>
      );
    }
    return (
      <AuthCard title={t('Link expired')} tagline={t('Build a bigger team. Stay your size.')}>
        <p
          style={{
            fontSize: 14,
            color: 'var(--color-text-muted)',
            lineHeight: 1.6,
            marginBottom: 16,
          }}
        >
          {t('This link has expired. Enter your email to get a new one.')}
        </p>
        <form onSubmit={(e) => void resendForm.handleSubmit(handleResend)(e)} noValidate>
          <TextField
            label={t('Email address')}
            type="email"
            autoComplete="email"
            error={resendForm.formState.errors.email?.message}
            {...resendForm.register('email')}
          />
          <div style={{ marginTop: 16 }}>
            <Button
              type="submit"
              variant="primary"
              size="lg"
              isLoading={resendForm.formState.isSubmitting}
            >
              {t('Send a new link →')}
            </Button>
          </div>
        </form>
      </AuthCard>
    );
  }

  return (
    <AuthCard
      title={t("You're almost in.")}
      subtitle={t('Click below to confirm your email address.')}
      tagline={t('Build a bigger team. Stay your size.')}
    >
      {pageState === 'error' ? (
        <p style={{ fontSize: 13, color: 'var(--color-danger)', marginBottom: 12 }}>
          {t('Something went wrong. Please try again.')}
        </p>
      ) : null}
      <Button
        variant="primary"
        size="lg"
        isLoading={pageState === 'confirming'}
        onClick={() => void handleConfirm()}
      >
        {pageState === 'confirming' ? t('Confirming…') : t('Confirm my account →')}
      </Button>
    </AuthCard>
  );
}
